import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ToggleSwitch from "../../Components/ToggleSwitch";
import {
  getNotificationSettings,
  updateNotificationSettings,
} from "../../actions/notifications";
import { getAssistantById, updateAssistant } from "../../actions/assistants";
import { Mail } from "lucide-react";

// Keyed on the API's own field names. The rows used to carry made-up ids, and
// two of them collided on "app-conversations" - so toggling the email row also
// flipped the in-app one, and vice versa.
const groups = [
  {
    id: "email",
    title: "Email notifications",
    items: [
      {
        id: "emailNewLead",
        label: "New lead captured",
        description:
          "Get an email each time the assistant captures a qualified lead.",
      },
      {
        id: "emailHandoffRequested",
        label: "Human handoff requested",
        description: "A visitor asked to talk to someone on your team.",
      },
      {
        id: "emailNewConversation",
        label: "New conversations",
        description: "Email me whenever a visitor starts chatting.",
      },
      {
        id: "emailPaymentReceived",
        label: "Payment received",
        description: "Send a receipt each time a subscription payment goes through.",
      },
    ],
  },
  {
    id: "inapp",
    title: "In-app notifications",
    subtitle: "Shown in the notification bell at the top of the dashboard.",
    items: [
      {
        id: "inAppNewConversation",
        label: "New conversations",
        description: "Ping me whenever a visitor starts chatting.",
      },
      {
        id: "inAppPaymentReceived",
        label: "Payment received",
        description: "Notify me when a subscription payment is taken.",
      },
      {
        id: "inAppUsageLimits",
        label: "Usage and limits",
        description:
          "Warn me when the workspace nears its monthly message limit.",
      },
    ],
  },
];

export default function NotificationsPanel() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  // The assistant the switcher is on: the email rows below are about its
  // visitors, so its own address is the one that matters.
  const assistantId = useSelector((state) => state.assistants.activeId);
  const [assistant, setAssistant] = useState(null);
  // The address this assistant's emails go to. Empty means "use the account
  // email", which is what the API stores as null.
  const [emailValue, setEmailValue] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState("");

  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // The row currently being saved, so only its switch shows as busy.
  const [saving, setSaving] = useState("");
  // The label of whatever was saved last, shown briefly. A toggle that moves
  // and says nothing gives no sign the change actually reached the server.
  const [saved, setSaved] = useState("");
  const savedTimer = useRef(null);

  useEffect(() => () => clearTimeout(savedTimer.current), []);

  useEffect(() => {
    let cancelled = false;

    dispatch(getNotificationSettings()).then((result) => {
      if (cancelled) return;

      setLoading(false);

      if (!result?.success) {
        setError(result?.message || "We couldn't load your notification settings.");
        return;
      }

      setSettings(result.settings || {});
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // Which assistant the app is pointed at, for the address its emails go to.
  useEffect(() => {
    if (!assistantId) return undefined;

    let cancelled = false;

    dispatch(getAssistantById(assistantId)).then((result) => {
      if (cancelled || !result?.success) return;

      setAssistant(result.assistant);
      setEmailValue(result.assistant?.notificationEmail || "");
    });

    return () => {
      cancelled = true;
    };
  }, [assistantId, dispatch]);

  // Flipped immediately and put back if the save fails: a switch that waits on
  // a round trip feels broken, and one that stays flipped after a failed save
  // is a lie.
  const toggle = async (id, label) => {
    const next = !settings[id];

    setSettings((prev) => ({ ...prev, [id]: next }));
    setSaving(id);
    setError("");
    setSaved("");
    clearTimeout(savedTimer.current);

    const result = await dispatch(updateNotificationSettings({ [id]: next }));

    setSaving("");

    if (!result?.success) {
      setSettings((prev) => ({ ...prev, [id]: !next }));
      setError(result?.message || "We couldn't save that change.");
      return;
    }

    setSaved(`${label} ${next ? "turned on" : "turned off"}.`);
    savedTimer.current = setTimeout(() => setSaved(""), 2500);

    // The response carries the saved record, so adopt it rather than trusting
    // the optimistic guess - that way any field the server adjusted alongside
    // this one is reflected instead of silently drifting.
    if (result.settings) setSettings(result.settings);
  };

  const saveEmail = async () => {
    setEmailBusy(true);
    setEmailError("");
    setSaved("");

    // An empty string is a real value here: it clears the override.
    const result = await dispatch(
      updateAssistant(assistantId, { notificationEmail: emailValue.trim() }),
    );

    setEmailBusy(false);

    if (!result?.success) {
      setEmailError(result?.message || "We couldn't save that address.");
      return;
    }

    setAssistant(result.assistant);
    setEmailValue(result.assistant?.notificationEmail || "");
    setSaved(
      result.assistant?.notificationEmail
        ? `Emails for this assistant now go to ${result.assistant.notificationEmail}.`
        : "Emails for this assistant now go to your account email.",
    );
  };

  // An assistant can send its mail somewhere else - sales@ for one site, the
  // owner for another - so the address shown is the one this assistant will
  // actually use, not the account's by default.
  const emailTo = assistant?.notificationEmail || user?.email;
  const emailSubtitle = emailTo
    ? `Sent to ${emailTo}${assistant?.notificationEmail ? ` for ${assistant.name || "this assistant"}` : ""}.`
    : "Sent to your account email.";

  if (loading) {
    return (
      <div className="acct-stack">
        {groups.map((group) => (
          <section className="dash-card" key={group.id} aria-busy="true">
            <span className="skel dash-live-skel--title" />
            <ul className="acct-toggle-list">
              {group.items.map((item) => (
                <li key={item.id}>
                  <div className="acct-toggle-text">
                    <span className="skel acct-skel--label" />
                    <span className="skel acct-skel--desc" />
                  </div>
                  <span className="skel acct-skel--switch" />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className="acct-stack">
      {error && <p className="auth-error">{error}</p>}
      {saved && (
        <p className="auth-success" role="status">
          {saved}
        </p>
      )}

      {groups.map((group) => (
        <section className="dash-card" key={group.id}>
          <h2 className="cz-card-title">{group.title}</h2>
          <p className="ist-card-subtitle">
            {group.id === "email" ? emailSubtitle : group.subtitle}
          </p>

          {/* The address those toggles send to. It belongs to the assistant,
              not the account, so an agency can route one site's leads to
              sales@ and another's to the owner. */}
          {group.id === "email" && assistantId && (
            <div className="acct-email-field">
              <span className="ist-field__input">
                <Mail size={16} strokeWidth={1.9} />
                <input
                  type="email"
                  value={emailValue}
                  placeholder={user?.email || "you@example.com"}
                  onChange={(e) => setEmailValue(e.target.value)}
                  disabled={emailBusy}
                  aria-label="Notification email for this assistant"
                />
              </span>

              <button
                type="button"
                className="onboarding__btn onboarding__btn--secondary"
                onClick={saveEmail}
                disabled={
                  emailBusy ||
                  emailValue.trim() === (assistant?.notificationEmail || "")
                }
              >
                {emailBusy ? "Saving…" : "Save"}
              </button>
            </div>
          )}

          {group.id === "email" && emailError && (
            <p className="auth-error">{emailError}</p>
          )}

          {group.id === "email" && assistantId && (
            <p className="acct-email-hint">
              Leave empty to use your account email
              {user?.email ? `, ${user.email}` : ""}.
            </p>
          )}

          <ul className="acct-toggle-list">
            {group.items.map((item) => (
              <li key={item.id}>
                <div className="acct-toggle-text">
                  <strong>{item.label}</strong>
                  <span>{item.description}</span>
                </div>
                <ToggleSwitch
                  checked={Boolean(settings[item.id])}
                  onChange={() => toggle(item.id, item.label)}
                  disabled={saving === item.id}
                  label={item.label}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
